import React, { useState, useEffect } from "react";
import { Navbar, Nav, Container, Form, Row, Col, Button } from "react-bootstrap";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/appContext";
import { isAdmin } from "../utils/permissions";
import { Loader } from "./Loader";
import { Message } from "./Message";
import LogoutIcon from "./LogoutIcon";

const Header = ({
  env,
  handleEnvChange,
  handleSearch,
  handleTenantFilter,
  handleUsersFilter,
  resetUsersFilters,
  resetTenantsFilters,
  tenants,
  groupVersions,
  loading,
  error,
  currentTenant,
  tenantName
}) => {
  const { user, activeKey, setActiveKey, confirmNavigation } = useAppContext();

  // Restore filter values from localStorage on initial mount
  const [tenantsSearchTerm, setTenantsSearchTerm] = useState(() => {
    return localStorage.getItem("tenantsSearchTerm") || "";
  });
  const [usersSearchTerm, setUsersSearchTerm] = useState(() => {
    return localStorage.getItem("usersSearchTerm") || "";
  });
  const [usersSelectedTenant, setUsersSelectedTenant] = useState(() => {
    return localStorage.getItem("usersSelectedTenant") || null;
  });

  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(false);
  const [prevEnv, setPrevEnv] = useState(env);
  const [prevUser, setPrevUser] = useState(user);

  const navigate = useNavigate();

  useEffect(() => {
    if (prevEnv && prevEnv !== env) {
      setTenantsSearchTerm("");
      setUsersSearchTerm("");
      setUsersSelectedTenant(null);
      localStorage.removeItem("tenantsSearchTerm");
      localStorage.removeItem("usersSearchTerm");
      localStorage.removeItem("usersSelectedTenant");
      handleSearch("");
      handleUsersFilter("", null);
    }
    if ((!prevUser && user) || (!prevEnv && env)) {
      setTenantsSearchTerm("");
      setUsersSearchTerm("");
      setUsersSelectedTenant(null);
      localStorage.removeItem("tenantsSearchTerm");
      localStorage.removeItem("usersSearchTerm");
      localStorage.removeItem("usersSelectedTenant");
      handleSearch("");
      handleUsersFilter("", null);
    }
    setPrevEnv(env);
    setPrevUser(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env, user]);

  // Admin has full permissions in every environment; viewer can never add anything.
  const showAddUser = isAdmin(user);
  const showAddTenant = isAdmin(user);

  const tenantsTabActive = activeKey === "/tenants";
  const usersTabActive = activeKey === "/users";
  const projectsTabActive = activeKey === "/projects" || activeKey === "/group-versions" || activeKey === "/version-categories";

  const versionsCategoriesTabActive = activeKey === "/version-categories";

  const measurementsTabActive = activeKey === "/measurements";

  const handleSelect = async (selectedKey) => {
    if (!(await confirmNavigation())) {
      return;
    }
    setActiveKey(selectedKey);
    navigate(selectedKey);

    if (selectedKey === "/users") {
      handleUsersFilter(usersSearchTerm, usersSelectedTenant);
    } else if (selectedKey === "/tenants") {
      handleSearch(tenantsSearchTerm);
    }
  };

  const handleEnvironmentSelect = async (selectedEnv) => {
    if (await confirmNavigation()) {
      handleEnvChange(selectedEnv, selectedTenant);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (usersTabActive) {
      handleUsersFilter(usersSearchTerm, usersSelectedTenant);
    } else if (tenantsTabActive) {
      handleSearch(tenantsSearchTerm);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (usersTabActive) {
      setUsersSearchTerm(value);
      localStorage.setItem("usersSearchTerm", value);
      if (value.trim() !== "") {
        setUsersSelectedTenant(null);
        localStorage.removeItem("usersSelectedTenant");
        handleUsersFilter(value, null);
      } else {
        handleUsersFilter(value, usersSelectedTenant);
      }
    } else if (tenantsTabActive) {
      setTenantsSearchTerm(value);
      localStorage.setItem("tenantsSearchTerm", value);
      handleSearch(value);
    }
  };

  const handleTenantSelect = (selectedOption) => {
    if (selectedOption) {
      const tenantInfo = {
        id: selectedOption?.value,
        name: selectedOption?.label
      };
      setSelectedTenant(tenantInfo.id);
      setSelectedVersion(null);
      handleEnvChange(env, tenantInfo.id, tenantInfo.name);
    } else {
      setSelectedTenant(null);
    }
  };

  const handleTenantFilterSelect = (selectedOption) => {
    if (selectedOption) {
      const tenantInfo = {
        id: selectedOption?.value,
        name: selectedOption?.label
      };
      setUsersSelectedTenant(tenantInfo.id);
      localStorage.setItem("usersSelectedTenant", tenantInfo.id);
      setUsersSearchTerm("");
      localStorage.removeItem("usersSearchTerm");
      handleUsersFilter("", tenantInfo.id);
    } else {
      setUsersSelectedTenant(null);
      localStorage.removeItem("usersSelectedTenant");
      handleUsersFilter(usersSearchTerm, null);
    }
  };

  const versionOptions = groupVersions
    ? groupVersions.map((groupVersion) => ({
        value: groupVersion.id,
        label: `${groupVersion.name} | ${groupVersion.ogmpYear}`
      }))
    : [];

  const handleVersionSelect = (selectedOption) => {
    if (selectedOption) {
      const versionInfo = {
        id: selectedOption?.value,
        name: selectedOption?.label
      };
      setSelectedVersion(versionInfo.id);
      handleEnvChange(env, currentTenant, tenantName, versionInfo.id);
    } else {
      setSelectedVersion(null);
    }
  };

  const username = user?.username;

  // format tenants for react-select
  const tenantOptions = tenants
    ? tenants.map((tenant) => ({
        value: tenant.id,
        label: tenant.name
      }))
    : [];

  return (
    <header className="w-100">
      <Container fluid className="px-0">
        <Row className="d-flex align-items-center justify-content-between header-row m-0 w-100">
          <Col className="d-flex justify-content-start">
            <h1 className="my-3 heading-title text-white">Foundry</h1>
          </Col>

          {username && (
            <Col className="d-flex justify-content-center">
              <Form className="d-flex align-items-center">
                <Form.Group controlId="form1" className="d-flex align-items-center">
                  <Form.Label className="text-primary mx-2 text-white" style={{ fontWeight: "bold" }}>
                    Environment
                  </Form.Label>
                  <Form.Control as="select" value={env} onChange={(e) => handleEnvironmentSelect(e.target.value)}>
                    <option value="dev">DEV</option>
                    <option value="qa">QA</option>
                    <option value="uat">UAT</option>
                    <option value="prod">PROD</option>
                    <option value="apUat">AP-UAT</option>
                    <option value="apProd">AP-PROD</option>
                  </Form.Control>
                </Form.Group>
              </Form>
            </Col>
          )}

          {username && (
            <Col className="d-flex justify-content-end">
              <div className="d-flex align-items-center">
                <h6 className="py-3 text-white mx-2 mb-0">
                  Logged in as <strong>{username}</strong>
                </h6>
                <LogoutIcon setSelectedTenant={setSelectedTenant} setUsersSelectedTenant={setUsersSelectedTenant} />
              </div>
            </Col>
          )}
        </Row>

        {username && (
          <Navbar className="w-100 p-0 mt-2">
            <Nav variant="underline" activeKey={activeKey} onSelect={handleSelect} className="w-100 d-flex justify-content-between">
              <div className="d-flex">
                <Nav.Item className="mr-3">
                  <Nav.Link eventKey="/tenants" style={{ fontSize: "1.25rem" }}>
                    Tenants
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item className="mx-3">
                  <Nav.Link eventKey="/users" style={{ fontSize: "1.25rem" }}>
                    Users
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item className="mx-3">
                  <Nav.Link eventKey="/projects" style={{ fontSize: "1.25rem" }}>
                    Projects
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item className="mx-3">
                  <Nav.Link eventKey="/measurements" style={{ fontSize: "1.25rem" }}>
                    Measurements
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item className="mx-3">
                  <Nav.Link eventKey="/system" style={{ fontSize: "1.25rem" }}>
                    System
                  </Nav.Link>
                </Nav.Item>
                {usersTabActive && (
                  <>
                    <Col className="d-flex mx-3">
                      <Form className="d-flex mx-auto" onSubmit={handleSearchSubmit}>
                        <Form.Control
                          type="search"
                          placeholder="Search user email"
                          className="me-sm-2 search-input"
                          value={usersSearchTerm}
                          onChange={handleInputChange}
                          style={{ minWidth: "250px" }}
                        />
                      </Form>
                    </Col>
                    <Col className="d-flex mx-3">
                      <Form className="d-flex mx-auto">
                        <Form.Group controlId="form2" className="d-flex align-items-center me-3">
                          {loading ? null : error ? (
                            <Message variant="danger">{error}</Message>
                          ) : (
                            <Select
                              options={tenantOptions}
                              value={tenantOptions?.find((opt) => opt.value === usersSelectedTenant) || null}
                              onChange={handleTenantFilterSelect}
                              placeholder="Filter by tenant"
                              className="tenant-select"
                              styles={{
                                control: (provided) => ({
                                  ...provided,
                                  width: "250px"
                                })
                              }}
                              isClearable
                              isSearchable
                            />
                          )}
                        </Form.Group>
                      </Form>
                    </Col>
                  </>
                )}
                {(projectsTabActive || measurementsTabActive) && (
                  <Col className="d-flex mx-3">
                    <Form className="d-flex mx-auto">
                      <Form.Group controlId="form2" className="d-flex align-items-center me-3">
                        {loading ? null : error ? (
                          <Message variant="danger">{error}</Message>
                        ) : (
                          <Select
                            options={tenantOptions}
                            value={tenantOptions?.find((opt) => opt.value === selectedTenant) || null}
                            onChange={handleTenantSelect}
                            placeholder="Choose tenant"
                            className="tenant-select"
                            styles={{
                              control: (provided) => ({
                                ...provided,
                                width: "250px"
                              })
                            }}
                            isClearable
                            isSearchable
                          />
                        )}
                      </Form.Group>
                    </Form>
                  </Col>
                )}
                {(versionsCategoriesTabActive || measurementsTabActive) && (
                  <Col className="d-flex mx-auto">
                    <Form className="d-flex mx-auto">
                      <Form.Group controlId="form3" className="d-flex align-items-center ms-4">
                        {loading ? null : error ? (
                          <Message variant="danger">{error}</Message>
                        ) : (
                          <Select
                            options={versionOptions}
                            value={versionOptions?.find((opt) => opt.value === selectedVersion) || null}
                            onChange={handleVersionSelect}
                            placeholder="Choose ogmp group version"
                            className="version-select"
                            styles={{
                              control: (provided) => ({
                                ...provided,
                                width: "280px"
                              })
                            }}
                            isClearable
                            isSearchable
                          />
                        )}
                      </Form.Group>
                    </Form>
                  </Col>
                )}

                {tenantsTabActive && (
                  <Form className="d-flex mx-auto ms-4" onSubmit={handleSearchSubmit}>
                    <Form.Control
                      type="search"
                      placeholder="Search tenant name"
                      className="me-sm-2 search-input"
                      value={tenantsSearchTerm}
                      onChange={handleInputChange}
                      style={{
                        minWidth: "250px"
                      }}
                    />
                  </Form>
                )}

                <div className="me-4" style={{ width: "20px" }}></div>

                {showAddTenant && tenantsTabActive && (
                  <div className="d-flex gap-2 mt-1 mb-1 ms-4" data-testid="tenant-actions">
                    <Button
                      style={{ backgroundColor: "#0e7864", borderColor: "#0e7864", color: "white", whiteSpace: "nowrap" }}
                      variant="primary"
                      onClick={() => navigate("/add-tenant")}
                    >
                      Add Tenant
                    </Button>
                  </div>
                )}

                {showAddUser && usersTabActive && (
                  <Button
                    style={{ backgroundColor: "#0e7864", borderColor: "#0e7864", color: "white", width: "50%" }}
                    className="mt-1 mb-1 ms-4"
                    onClick={() => navigate("/add-user")}
                  >
                    Add User
                  </Button>
                )}
              </div>
            </Nav>
          </Navbar>
        )}

        {loading && <Loader />}
        {error && <Message variant="danger">{error}</Message>}
      </Container>
    </header>
  );
};

export { Header };
